import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { subaccountDefundMax } from "../../utils/amount/max/subaccountMax";
import { ethers } from "ethers";
import { BaseModal } from "../BaseModal";
import { useSubaccounts } from "../../hooks/useSubaccounts";
import {
  floorToDecimalsString,
  isValidDecimalInput,
  toTokenUnits,
} from "../../utils/decimalString";
import { Props } from "./SubaccountsModal.types";
import {
  short,
  Body,
  Intro,
  Muted,
  CreateRow,
  Input,
  PrimaryBtn,
  Card,
  CardTop,
  Label,
  Addr,
  Balance,
  Row,
  SmallBtn,
  CardActions,
  ActiveTag,
  ActiveBanner,
  LinkBtn,
  StatusText,
} from "./SubaccountsModal.helpers";

/**
 * Subaccount management modal. Create in-app subaccounts (gasless two-signature link), move collateral
 * between the master and each subaccount (gasless internal transfers), and unlink. Funds movement is
 * always owner-signed — a leaked session key can never move funds.
 */
export function SubaccountsModal({ show, onHide }: Props) {
  const {
    supported, subs, balances, usdcDecimals, busy, error,
    create, fund, defund, unlink, forget,
    tradeAs, tradeAsMaster, activeSubaccount,
  } = useSubaccounts();

  const [label, setLabel] = useState("");
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<string | null>(null);

  const setAmt = (addr: string, v: string) => setAmounts((m) => ({ ...m, [addr]: v }));
  const { t: inputsT } = useTranslation("app", { keyPrefix: "common.inputs" });

  /** The subaccount's free balance, or why it cannot be offered. */
  const subMax = useMemo(
    () => (addr: string) => {
      const raw = balances[addr.toLowerCase()];
      return subaccountDefundMax({
        subFreeUnits: raw === undefined ? undefined : BigInt(raw),
        usdcDecimals,
      });
    },
    [balances, usdcDecimals]
  );

  const fmtBalance = useMemo(
    () => (addr: string) => {
      const raw = balances[addr.toLowerCase()];
      // Unknown is "—": it used to read "0", which is a different statement.
      if (raw === undefined) return "—";
      try {
        // Floored to 2dp first: toLocaleString rounds, so 9.999 read "10".
        const floored = floorToDecimalsString(ethers.utils.formatUnits(raw, usdcDecimals), 2);
        return Number(floored).toLocaleString(undefined, {
          maximumFractionDigits: 2,
        });
      } catch {
        return "—";
      }
    },
    [balances, usdcDecimals]
  );

  // A typed amount in USDC base units, or null when it is not plain decimal text
  // within USDC's decimals (the hook would hand it to `parseUnits` and throw).
  const amountUnits = (addr: string) => {
    const v = amounts[addr] || "";
    const units = isValidDecimalInput(v, usdcDecimals) ? toTokenUnits(v, usdcDecimals) : null;
    return units && units.gt(0) ? units : null;
  };

  /** True only when the balance is known AND the typed amount is above it. */
  const exceedsSubBalance = (addr: string) => {
    const max = subMax(addr);
    const units = amountUnits(addr);
    if (!max.ok || !units) return false;
    const maxUnits = toTokenUnits(max.max, usdcDecimals);
    return Boolean(maxUnits && units.gt(maxUnits));
  };

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setStatus(null);
    try {
      await fn();
      setStatus(ok);
    } catch (e: any) {
      setStatus(e?.message || "Action failed");
    }
  };

  return (
    <BaseModal show={show} onHide={onHide} title="Subaccounts">
      <Body>
        {!supported ? (
          <Muted>Subaccounts aren’t available on this network.</Muted>
        ) : (
          <>
            <Intro>
              Subaccounts are separate on-chain trading accounts your wallet controls. Move collateral
              in and out gaslessly — funds always require your wallet’s signature, never a session key.
              Trading as a subaccount is <b>perpetuals-only</b>.
            </Intro>

            {activeSubaccount && (
              <ActiveBanner>
                <span>
                  Trading as <b>{short(activeSubaccount)}</b>
                </span>
                <SmallBtn onClick={() => run(async () => tradeAsMaster(), "Back on main account")}>
                  Back to main account
                </SmallBtn>
              </ActiveBanner>
            )}

            <CreateRow>
              <Input
                placeholder="Label (optional)"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
              <PrimaryBtn
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    await create(label.trim() || undefined);
                    setLabel("");
                  }, "Subaccount created")
                }
              >
                {busy ? "Working…" : "+ Create subaccount"}
              </PrimaryBtn>
            </CreateRow>

            {subs.length === 0 ? (
              <Muted>No subaccounts yet. Create one to get started.</Muted>
            ) : (
              subs.map((s) => (
                <Card key={s.address}>
                  <CardTop>
                    <div>
                      <Label>{s.label}</Label>
                      <Addr title={s.address}>{short(s.address)}</Addr>
                    </div>
                    <Balance>
                      {fmtBalance(s.address)} <span>USDC</span>
                    </Balance>
                  </CardTop>

                  <Row>
                    <Input
                      inputMode="decimal"
                      placeholder="Amount"
                      value={amounts[s.address] || ""}
                      onChange={(e) => setAmt(s.address, e.target.value)}
                    />
                    <SmallBtn
                      // Fills the subaccount's own balance, so Withdraw has a
                      // MAX like every other funds field. Disabled, with the
                      // reason, while the balance is unknown.
                      disabled={busy || !subMax(s.address).ok}
                      title={
                        subMax(s.address).ok
                          ? undefined
                          : inputsT(`max_unavailable.${(subMax(s.address) as { reason: string }).reason}`)
                      }
                      onClick={() => {
                        const max = subMax(s.address);
                        if (max.ok) setAmt(s.address, max.max);
                      }}
                    >
                      MAX
                    </SmallBtn>
                    <SmallBtn
                      disabled={busy || !amountUnits(s.address)}
                      onClick={() =>
                        run(async () => {
                          await fund(s.address, amounts[s.address]);
                          setAmt(s.address, "");
                        }, "Funded")
                      }
                    >
                      Fund →
                    </SmallBtn>
                    <SmallBtn
                      // Capped by the balance only when it is KNOWN: a failed
                      // read used to be stored as "0", which is why this could
                      // not be capped at all. Unknown still leaves it to the
                      // chain, as before.
                      disabled={
                        busy ||
                        !amountUnits(s.address) ||
                        exceedsSubBalance(s.address)
                      }
                      onClick={() =>
                        run(async () => {
                          await defund(s.address, amounts[s.address]);
                          setAmt(s.address, "");
                        }, "Withdrawn")
                      }
                    >
                      ← Withdraw
                    </SmallBtn>
                  </Row>

                  <CardActions>
                    {activeSubaccount?.toLowerCase() === s.address.toLowerCase() ? (
                      <ActiveTag>● Active</ActiveTag>
                    ) : (
                      <LinkBtn
                        disabled={busy}
                        onClick={() => run(() => tradeAs(s.address), "Now trading as this subaccount")}
                      >
                        Trade as
                      </LinkBtn>
                    )}
                    <LinkBtn disabled={busy} onClick={() => run(() => unlink(s.address), "Unlinked")}>
                      Unlink
                    </LinkBtn>
                    <LinkBtn onClick={() => forget(s.address)}>Forget locally</LinkBtn>
                  </CardActions>
                </Card>
              ))
            )}

            {(status || error) && <StatusText>{status || error}</StatusText>}
          </>
        )}
      </Body>
    </BaseModal>
  );
}
